// components/ScheduleHistoryViewer.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Paper,
  Tabs,
  Tab,
  Collapse,
  Tooltip,
  Badge,
  LinearProgress,
  Menu,
  MenuItem,
  Grid
} from '@material-ui/core';
import {
  History,
  Restore,
  SwapHoriz,
  Tune,
  Group,
  Search,
  ExpandMore,
  ExpandLess,
  FilterList,
  GetApp,
  CheckCircle,
  Warning,
  Info,
  Schedule,
  TrendingUp,
  Undo,
  CloudUpload
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { scheduleHistory } from '../utils/scheduleHistory';

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    height: '70vh',
    padding: 0
  },
  header: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  content: {
    height: 'calc(100% - 64px)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  filterBar: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  timeline: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(2)
  },
  timelineItem: {
    cursor: 'pointer',
    '&:hover': {
      '& $timelineContent': {
        backgroundColor: theme.palette.action.hover
      }
    }
  },
  timelineContent: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.3s ease'
  },
  expandedContent: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  statsCard: {
    padding: theme.spacing(2),
    textAlign: 'center'
  },
  restoreButton: {
    marginTop: theme.spacing(1)
  },
  impactChip: {
    margin: theme.spacing(0.5)
  }
}));

const ScheduleHistoryViewer = ({ 
  open, 
  onClose, 
  onRestore,
  currentState 
}) => {
  const classes = useStyles();
  const [tabValue, setTabValue] = useState(0);
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  // Load history on mount
  useEffect(() => {
    if (open) {
      loadHistory();
      loadStats();
    }
  }, [open]);

  // Filter history when search or filter changes
  useEffect(() => {
    filterHistory();
  }, [searchTerm, filterType, history]);

  const loadHistory = () => {
    const data = scheduleHistory.getHistory();
    setHistory(data);
  };

  const loadStats = () => {
    const statsData = scheduleHistory.getHistoryStats();
    setStats(statsData);
  };

  const filterHistory = () => {
    let filtered = [...history];

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(item => item.type === filterType);
    }

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.description.toLowerCase().includes(search) ||
        item.user.toLowerCase().includes(search) ||
        item.type.toLowerCase().includes(search)
      );
    }

    setFilteredHistory(filtered);
  };

  const toggleExpanded = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleRestore = async (item) => {
    setLoading(true);
    try {
      const restoredState = scheduleHistory.restoreToPoint(item.id, currentState);
      
      // Record the restore action
      scheduleHistory.recordChange({
        type: 'restore',
        description: `Restored to: ${item.description}`,
        metadata: {
          restoredToId: item.id,
          restoredToTimestamp: item.timestamp
        }
      });

      if (onRestore) {
        await onRestore(restoredState, item);
      }
      
      onClose();
    } catch (error) {
      console.error('Restore failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'job_transfer': return <SwapHoriz color="primary" />;
      case 'route_optimization': return <Tune color="primary" />;
      case 'crew_assignment': return <Group color="primary" />;
      case 'restore_point': return <CloudUpload color="secondary" />;
      case 'restore': return <Restore color="action" />;
      case 'transaction': return <TrendingUp color="primary" />;
      default: return <History color="action" />;
    }
  };

  const getTimeAgo = (timestamp) => {
    return moment(timestamp).fromNow();
  };

  const formatTimestamp = (timestamp) => {
    return moment(timestamp).format('MMM D, YYYY h:mm A');
  };

  const exportHistory = () => {
    const csv = scheduleHistory.exportHistory(filteredHistory);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `schedule_history_${moment().format('YYYY-MM-DD')}.csv`;
    a.click();
  };

  const renderTimelineView = () => (
    <Box className={classes.timeline}>
      {filteredHistory.map((item, index) => {
        const isExpanded = expandedItems[item.id];
        const isEven = index % 2 === 0;
        
        return (
          <Box key={item.id} className={classes.timelineItem} mb={3}>
            <Grid container spacing={2} alignItems="flex-start">
              {/* Left side - timestamp (for even items) */}
              <Grid item xs={5} style={{ textAlign: isEven ? 'right' : 'left', order: isEven ? 0 : 2 }}>
                {isEven && (
                  <Box pr={2}>
                    <Typography variant="body2" color="textSecondary">
                      {getTimeAgo(item.timestamp)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {formatTimestamp(item.timestamp)}
                    </Typography>
                  </Box>
                )}
                {!isEven && (
                  <Box pl={2}>
                    <Paper 
                      className={classes.timelineContent}
                      onClick={() => toggleExpanded(item.id)}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box flex={1}>
                          <Typography variant="subtitle2" gutterBottom>
                            {item.description}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip 
                              size="small" 
                              label={item.type.replace('_', ' ')} 
                              variant="outlined"
                            />
                            <Typography variant="caption" color="textSecondary">
                              by {item.user}
                            </Typography>
                          </Box>
                          
                          {item.metadata?.impact && (
                            <Box mt={1}>
                              <Chip
                                size="small"
                                label={item.metadata.impact}
                                className={classes.impactChip}
                                icon={<TrendingUp />}
                                color="primary"
                              />
                            </Box>
                          )}
                        </Box>
                        
                        <IconButton size="small">
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Box>
                      
                      <Collapse in={isExpanded}>
                        <Box className={classes.expandedContent}>
                          {item.type === 'job_transfer' && item.metadata && (
                            <Box>
                              <Typography variant="body2" paragraph>
                                <strong>Jobs Transferred:</strong> {item.metadata.jobCount}
                              </Typography>
                              <Typography variant="body2" paragraph>
                                <strong>Hours Moved:</strong> {item.metadata.hoursTransferred}h
                              </Typography>
                              <Typography variant="body2" paragraph>
                                <strong>From:</strong> Crew {item.metadata.fromCrewId}
                              </Typography>
                              <Typography variant="body2" paragraph>
                                <strong>To:</strong> Crew {item.metadata.toCrewId}
                              </Typography>
                            </Box>
                          )}
                          
                          {item.type === 'restore_point' && item.metadata && (
                            <Box>
                              <Typography variant="body2" paragraph>
                                <strong>Crews:</strong> {item.metadata.crewCount}
                              </Typography>
                              <Typography variant="body2" paragraph>
                                <strong>Routes:</strong> {item.metadata.routeCount}
                              </Typography>
                              <Typography variant="body2" paragraph>
                                <strong>Sites:</strong> {item.metadata.siteCount}
                              </Typography>
                            </Box>
                          )}
                          
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<Restore />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRestore(item);
                            }}
                            className={classes.restoreButton}
                            disabled={loading}
                          >
                            Restore to This Point
                          </Button>
                        </Box>
                      </Collapse>
                    </Paper>
                  </Box>
                )}
              </Grid>
              
              {/* Center - timeline dot and connector */}
              <Grid item xs={2} style={{ order: 1 }}>
                <Box display="flex" flexDirection="column" alignItems="center" height="100%">
                  <Box 
                    width={40} 
                    height={40} 
                    borderRadius="50%"
                    bgcolor={item.type === 'restore_point' ? 'secondary.main' : 'primary.main'}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    boxShadow={2}
                  >
                    {getIcon(item.type)}
                  </Box>
                  {index < filteredHistory.length - 1 && (
                    <Box
                      width={2}
                      flex={1}
                      bgcolor="grey.300"
                      mt={1}
                    />
                  )}
                </Box>
              </Grid>
              
              {/* Right side - content (for even items) */}
              <Grid item xs={5} style={{ textAlign: isEven ? 'left' : 'right', order: isEven ? 2 : 0 }}>
                {isEven && (
                  <Box pl={2}>
                    <Paper 
                      className={classes.timelineContent}
                      onClick={() => toggleExpanded(item.id)}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box flex={1}>
                          <Typography variant="subtitle2" gutterBottom>
                            {item.description}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip 
                              size="small" 
                              label={item.type.replace('_', ' ')} 
                              variant="outlined"
                            />
                            <Typography variant="caption" color="textSecondary">
                              by {item.user}
                            </Typography>
                          </Box>
                          
                          {item.metadata?.impact && (
                            <Box mt={1}>
                              <Chip
                                size="small"
                                label={item.metadata.impact}
                                className={classes.impactChip}
                                icon={<TrendingUp />}
                                color="primary"
                              />
                            </Box>
                          )}
                        </Box>
                        
                        <IconButton size="small">
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Box>
                      
                      <Collapse in={isExpanded}>
                        <Box className={classes.expandedContent}>
                          {item.type === 'job_transfer' && item.metadata && (
                            <Box>
                              <Typography variant="body2" paragraph>
                                <strong>Jobs Transferred:</strong> {item.metadata.jobCount}
                              </Typography>
                              <Typography variant="body2" paragraph>
                                <strong>Hours Moved:</strong> {item.metadata.hoursTransferred}h
                              </Typography>
                              <Typography variant="body2" paragraph>
                                <strong>From:</strong> Crew {item.metadata.fromCrewId}
                              </Typography>
                              <Typography variant="body2" paragraph>
                                <strong>To:</strong> Crew {item.metadata.toCrewId}
                              </Typography>
                            </Box>
                          )}
                          
                          {item.type === 'restore_point' && item.metadata && (
                            <Box>
                              <Typography variant="body2" paragraph>
                                <strong>Crews:</strong> {item.metadata.crewCount}
                              </Typography>
                              <Typography variant="body2" paragraph>
                                <strong>Routes:</strong> {item.metadata.routeCount}
                              </Typography>
                              <Typography variant="body2" paragraph>
                                <strong>Sites:</strong> {item.metadata.siteCount}
                              </Typography>
                            </Box>
                          )}
                          
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<Restore />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRestore(item);
                            }}
                            className={classes.restoreButton}
                            disabled={loading}
                          >
                            Restore to This Point
                          </Button>
                        </Box>
                      </Collapse>
                    </Paper>
                  </Box>
                )}
                {!isEven && (
                  <Box pr={2}>
                    <Typography variant="body2" color="textSecondary">
                      {getTimeAgo(item.timestamp)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {formatTimestamp(item.timestamp)}
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        );
      })}
    </Box>
  );

  const renderListView = () => (
    <List className={classes.timeline}>
      {filteredHistory.map((item) => (
        <ListItem 
          key={item.id}
          button
          onClick={() => setSelectedItem(item)}
        >
          <ListItemIcon>
            {getIcon(item.type)}
          </ListItemIcon>
          <ListItemText
            primary={item.description}
            secondary={
              <React.Fragment>
                <Typography variant="caption" component="span">
                  {formatTimestamp(item.timestamp)} • by {item.user}
                </Typography>
                {item.metadata?.impact && (
                  <Typography variant="caption" display="block" color="primary">
                    {item.metadata.impact}
                  </Typography>
                )}
              </React.Fragment>
            }
          />
          <ListItemSecondaryAction>
            <Tooltip title="Restore to this point">
              <IconButton 
                edge="end" 
                onClick={() => handleRestore(item)}
                disabled={loading}
              >
                <Restore />
              </IconButton>
            </Tooltip>
          </ListItemSecondaryAction>
        </ListItem>
      ))}
    </List>
  );

  const renderStatsView = () => (
    <Box p={3}>
      <Grid container spacing={3}>
        <Grid item xs={3}>
          <Paper className={classes.statsCard}>
            <Typography variant="h4" color="primary">
              {stats?.totalChanges || 0}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total Changes
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={3}>
          <Paper className={classes.statsCard}>
            <Typography variant="h4" color="primary">
              {stats?.changesLast24h || 0}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Last 24 Hours
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={3}>
          <Paper className={classes.statsCard}>
            <Typography variant="h4" color="primary">
              {stats?.changesLast7d || 0}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Last 7 Days
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={3}>
          <Paper className={classes.statsCard}>
            <Typography variant="h4" color="primary">
              {moment(stats?.oldestChange).format('MMM D')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              History Since
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {stats?.changesByType && (
        <Box mt={3}>
          <Typography variant="h6" gutterBottom>
            Changes by Type
          </Typography>
          {Object.entries(stats.changesByType).map(([type, count]) => (
            <Box key={type} display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Box display="flex" alignItems="center" gap={1}>
                {getIcon(type)}
                <Typography variant="body2">
                  {type.replace('_', ' ').charAt(0).toUpperCase() + type.slice(1)}
                </Typography>
              </Box>
              <Chip label={count} size="small" />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Schedule History
          </Typography>
          <Box>
            <Tooltip title="Create restore point">
              <IconButton 
                onClick={() => {
                  scheduleHistory.createRestorePoint(
                    currentState,
                    `Manual checkpoint - ${moment().format('MMM D, h:mm A')}`
                  );
                  loadHistory();
                }}
              >
                <CloudUpload />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export history">
              <IconButton onClick={exportHistory}>
                <GetApp />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Box className={classes.content}>
          <Paper square elevation={0}>
            <Tabs
              value={tabValue}
              onChange={(e, val) => setTabValue(val)}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab icon={<History />} label="Timeline" />
              <Tab icon={<FilterList />} label="List View" />
              <Tab icon={<TrendingUp />} label="Statistics" />
            </Tabs>
          </Paper>

          {tabValue !== 2 && (
            <Box className={classes.filterBar}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search history..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Filter by type"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <MenuItem value="all">All Changes</MenuItem>
                    <MenuItem value="job_transfer">Job Transfers</MenuItem>
                    <MenuItem value="route_optimization">Route Optimizations</MenuItem>
                    <MenuItem value="crew_assignment">Crew Assignments</MenuItem>
                    <MenuItem value="restore_point">Restore Points</MenuItem>
                    <MenuItem value="restore">Restores</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
              
              <Box mt={1}>
                <Typography variant="caption" color="textSecondary">
                  Showing {filteredHistory.length} of {history.length} changes
                </Typography>
              </Box>
            </Box>
          )}

          {loading && <LinearProgress />}

          {tabValue === 0 && renderTimelineView()}
          {tabValue === 1 && renderListView()}
          {tabValue === 2 && renderStatsView()}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleHistoryViewer;